import { Dictionary, reduce } from 'lodash';
import TimeOffType from 'types/enum/TimeOffType';
import ITimeOff from 'types/TimeOff';
import { Nullable } from 'types/util/Nullable';
import { t } from './localize';
import { add } from './numberUtils';
import Papa from 'papaparse';
import ITimeOffRequest from 'types/TimeOffRequest';
import ICursorable from 'types/Cursorable';
import { IMember } from 'types/Member';
import { dateTimeFromISOWithoutZone, getDateName, getDateString, getHourMinuteStringFromSeconds } from './dateUtils';
import { DateTime } from 'luxon';
import { TimeOffRequestRowStatus } from 'containers/time-off-requests/TimeOffRequestTable/TimeOffRequestTable';
import { getFullNameFromMember } from './stringUtils';

export function getTimeOffTypeTitle(type: TimeOffType) {
  switch (type) {
    case TimeOffType.HOLIDAY:
      return t('Holiday');
    case TimeOffType.PERSONAL:
      return t('Personal');
    case TimeOffType.SICK:
      return t('Sick');
    case TimeOffType.VACATION:
      return t('Vacation');
    case TimeOffType.OTHER:
      return t('Other');
    default:
      return t('Other');
  }
}

export function getPaidSecondsFromTimeOff<T extends Pick<ITimeOff, 'paid' | 'seconds'>>(timeOff: T) {
  if (timeOff.paid) {
    return timeOff.seconds ?? 0;
  }

  return 0;
}

export function getUnpaidSecondsFromTimeOff<T extends Pick<ITimeOff, 'paid' | 'seconds'>>(timeOff: T) {
  if (!timeOff.paid) {
    return timeOff.seconds ?? 0;
  }

  return 0;
}

export function getSecondsFromTimeOff<T extends Pick<ITimeOff, 'paid' | 'seconds'>>(
  timeOff: T,
  paid: Nullable<boolean>
) {
  if (paid === true) {
    return getPaidSecondsFromTimeOff(timeOff);
  } else if (paid === false) {
    return getUnpaidSecondsFromTimeOff(timeOff);
  } else {
    return timeOff.seconds ?? 0;
  }
}

export type AggregatedTimeOff = {
  holidaySeconds: number;
  otherSeconds: number;
  personalSeconds: number;
  sickSeconds: number;
  vacationSeconds: number;
};

export function aggregateTimeOffByType<T extends Pick<ITimeOff, 'paid' | 'seconds' | 'type'>>(
  timeOffs: T[],
  paid: Nullable<boolean>
): AggregatedTimeOff {
  return reduce(
    timeOffs,
    (acc, cur) => ({
      holidaySeconds: add(acc.holidaySeconds, cur.type === TimeOffType.HOLIDAY ? getSecondsFromTimeOff(cur, paid) : 0),
      otherSeconds: add(acc.otherSeconds, cur.type === TimeOffType.OTHER ? getSecondsFromTimeOff(cur, paid) : 0),
      personalSeconds: add(
        acc.personalSeconds,
        cur.type === TimeOffType.PERSONAL ? getSecondsFromTimeOff(cur, paid) : 0
      ),
      sickSeconds: add(acc.sickSeconds, cur.type === TimeOffType.SICK ? getSecondsFromTimeOff(cur, paid) : 0),
      vacationSeconds: add(
        acc.vacationSeconds,
        cur.type === TimeOffType.VACATION ? getSecondsFromTimeOff(cur, paid) : 0
      ),
    }),
    {
      holidaySeconds: 0,
      otherSeconds: 0,
      personalSeconds: 0,
      sickSeconds: 0,
      vacationSeconds: 0,
    }
  );
}

function getStatus(approved?: Nullable<boolean>): string {
  if (approved) {
    return 'Approved';
  } else if (approved === false) {
    return 'Denied';
  } else {
    return 'Pending';
  }
}

export function convertTimeOffRequestDataToCSV(rows: (ITimeOffRequest & ICursorable)[]) {
  const headers = [
    t('Date'),
    t('Approval'),
    t('Employee'),
    t('Processed By'),
    t('Answered On'),
    t('Total'),
    t('Pay'),
    t('Type'),
    t('Description'),
    t('Requested On'),
  ];
  const csv = Papa.unparse(
    rows.map((row: ITimeOffRequest & ICursorable) => {
      const empName = getFullNameFromMember(row.member) ?? '';
      const processedByName = row.answeredByMember ? getFullNameFromMember(row.answeredByMember) : '';
      const result: Dictionary<string | boolean | TimeOffType | IMember | null | undefined> = {
        [t('Date')]: getDateName(DateTime.fromISO(row.dateStamp), 'ccc, LLL d'),
        [t('Approval')]: getStatus(row.approved),
        [t('Employee')]: empName,
        [t('Processed By')]: processedByName,
        [t('Answered On')]: row.answeredOn
          ? (getDateString(dateTimeFromISOWithoutZone(row.answeredOn), 'ccc, LLL d  @ t', true) ?? '')
          : '',
        [t('Total')]: getHourMinuteStringFromSeconds(row.seconds ?? 0),
        [t('Pay')]: row.paid === true ? t('Paid') : t('Unpaid'),
        [t('Type')]: getTimeOffTypeTitle(row.type),
        [t('Description')]: row.description ?? '',
        [t('Requested On')]: getDateString(DateTime.fromISO(row.createdOnLocal), 'ccc, LLL d  @ t', true) ?? '',
      };

      return result;
    }),
    {
      header: true,
      columns: headers,
    }
  );

  return csv;
}
